'use client'

import { useEffect, useState } from 'react'

import { useConditionalNotification } from '@hooks/useConditionalNotification'

const CONNECTION_NOTIFICATION_ID = 'connection-lost'

/**
 * Monitors browser online/offline state and shows a notification in the bell
 * when the connection is lost. Auto-dismisses when back online.
 *
 * Example of `useConditionalNotification` pattern — see NOTIFICATIONS.md.
 */
export function useConnectionStatus() {
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true))

  useEffect(() => {
    const goOnline = () => setIsOnline(true)
    const goOffline = () => setIsOnline(false)

    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)

    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  useConditionalNotification({
    id: CONNECTION_NOTIFICATION_ID,
    isActive: !isOnline,
    payload: !isOnline
      ? {
          id: CONNECTION_NOTIFICATION_ID,
          type: 'error',
          title: 'Connection Lost',
          message: 'You are offline. Some features may not work.',
          persistent: true,
        }
      : null,
  })

  return { isOnline }
}
